import { FC, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { PopulationService } from "@services/engagements/population/populationService";
import { EngagementService } from "@services/engagements/engagementService";
import type { SegmentLinkLaunch } from "@services/engagements/commands/cmdGetSegmentLinks";
import type { BilledPeriods } from "@models/population.model";

const service = new PopulationService();

interface Props {
  populationSegmentId: number;
  populationName: string;
  /** This engagement's campaigns on the population — the "clear go-live date" rows. */
  launches?: readonly SegmentLinkLaunch[];
  /** A reset was started — the host re-derives what it shows about the population. */
  onChanged: () => void;
  /** A campaign's go-live date was cleared — the host reloads the segment links. */
  onGoLiveCleared: () => void;
}

type Action = { kind: "all" } | { kind: "test" } | { kind: "go-live"; launch: SegmentLinkLaunch };

const formatDay = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/*
    The population's destructive actions, GitHub-style (MPN-1674): one place, one
    action per row, each behind a confirmation that says exactly what goes.

    It sits under the Files list, not on the Members tab, because every reason to
    wipe a population traces back to a file, and the recovery — discard the bad
    file, upload the right one, process — is all file actions. Rebuilding is NOT
    here: "Reprocess all files" above is the rebuild.

    "Remove everything" is offered after publish too (MPN-1752). It is the three
    deletions and nothing else — no replay, nothing sent to a network — and the
    confirmation lists the counts and what to do afterwards. The one cost is stated
    there: network audiences keep whoever is already uploaded, so a republish adds
    the new members but cannot remove anyone the new file drops.
*/
export const PopulationDangerZone: FC<Props> = ({
  populationSegmentId,
  populationName,
  launches,
  onChanged,
  onGoLiveCleared,
}) => {
  // What a reset would delete and put at risk (MPN-1516, MPN-1752). Null until it
  // answers; the buttons stay disabled rather than guessing.
  const [preflight, setPreflight] = useState<BilledPeriods | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);

  const [confirming, setConfirming] = useState<Action | null>(null);
  const [typedName, setTypedName] = useState("");
  const [overrideAck, setOverrideAck] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<{ kind: "info" | "danger"; text: string } | null>(null);
  // Closed until asked (MPN-1815): the label says what lives here, the rows appear
  // on a click, so the destructive actions are never in view next to "Reprocess".
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setPreflight(null);
    setPreflightError(null);
    service
      .getBilledPeriods(populationSegmentId)
      .then(setPreflight)
      .catch((err) => setPreflightError(err instanceof Error ? err.message : "Could not check this population"));
  }, [populationSegmentId]);

  const published = preflight?.published ?? false;
  const testCount = preflight?.test_count ?? 0;
  const traceCount = preflight?.trace_count ?? 0;
  const populationCount = preflight?.population_count ?? 0;
  const audiences = preflight?.audiences ?? [];
  const billedMonths = preflight?.months ?? [];
  const requiresOverride = preflight?.requires_override ?? false;
  const datedLaunches = (launches ?? []).filter((l) => l.go_live_date);

  const close = () => {
    setConfirming(null);
    setTypedName("");
    setOverrideAck(false);
    setOverrideReason("");
  };

  const overrideReady = !requiresOverride || (overrideAck && overrideReason.trim().length > 0);
  const canConfirm =
    !running &&
    (confirming?.kind === "test" ||
      confirming?.kind === "go-live" ||
      (typedName.trim() === populationName && overrideReady));

  const run = async () => {
    const action = confirming;
    if (!action) return;
    setRunning(true);
    setMessage(null);
    try {
      if (action.kind === "go-live") {
        await new EngagementService().setLaunchGoLiveDate(action.launch.id, null);
        setMessage({
          kind: "info",
          text: `Go-live date cleared for ${action.launch.name}. Files now load as arrival state; the next non-paused publish sets a new date.`,
        });
        close();
        onGoLiveCleared();
        return;
      }
      const { job_id } = await service.resetSegmentData(populationSegmentId, {
        testOnly: action.kind === "test",
        // Test-row removal never reaches the billed-period guard (synthetic rows are
        // not billable), so it never carries an override.
        overrideBilledPeriod: action.kind === "all" && requiresOverride ? true : undefined,
        overrideReason: action.kind === "all" && requiresOverride ? overrideReason.trim() : undefined,
      });
      setMessage({
        kind: "info",
        text:
          action.kind === "test"
            ? `Removing test rows (job #${job_id}) — their membership, audience members and conversions go with them.`
            : `Reset started (job #${job_id}) — conversions, audience members and population members are being removed. Then: load a file, recalculate the audiences, republish.`,
      });
      close();
      onChanged();
    } catch (err) {
      setMessage({ kind: "danger", text: err instanceof Error ? err.message : "Action could not start" });
    } finally {
      setRunning(false);
    }
  };

  const title = () => {
    switch (confirming?.kind) {
      case "test":
        return `Remove test rows from ${populationName}?`;
      case "go-live":
        return `Clear the go-live date for ${confirming.launch.name}?`;
      default:
        return `Remove everything from ${populationName}?`;
    }
  };

  const confirmLabel = () => {
    if (running) return "Starting…";
    switch (confirming?.kind) {
      case "test":
        return "Remove test rows";
      case "go-live":
        return "Clear go-live date";
      default:
        return "Remove everything";
    }
  };

  return (
    <div className="danger-zone">
      <button
        type="button"
        className="danger-zone__title danger-zone__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="danger-zone__chevron" aria-hidden="true">{open ? "▾" : "▸"}</span>
        Danger Zone
      </button>

      {open && (
        <>
      {message && (
        <div className={`alert alert-${message.kind} py-2 small mb-0 mx-3 mt-3`}>{message.text}</div>
      )}
      {preflightError && <div className="alert alert-warning py-2 small mb-0 mx-3 mt-3">{preflightError}</div>}

      <div className="danger-zone__row">
        <div>
          <div className="danger-zone__label">Remove everything</div>
          <div className="danger-zone__copy">
            Deletes this population&apos;s conversions, audience members and population members, including
            the control group. The population definition, audience rules and files stay.
          </div>
        </div>
        <div className="danger-zone__action">
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            style={{ fontSize: "0.72rem" }}
            disabled={preflight === null || running}
            onClick={() => setConfirming({ kind: "all" })}
          >
            Remove everything…
          </button>
        </div>
      </div>

      {testCount > 0 && (
        <div className="danger-zone__row">
          <div>
            <div className="danger-zone__label">Remove test rows</div>
            <div className="danger-zone__copy">
              Deletes the {testCount.toLocaleString()} rows flagged as test — their membership,
              audience members and conversions. Real rows are untouched, and this is safe after publish.
            </div>
          </div>
          <div className="danger-zone__action">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: "0.72rem" }}
              disabled={running}
              onClick={() => setConfirming({ kind: "test" })}
            >
              Remove test rows…
            </button>
          </div>
        </div>
      )}

      {datedLaunches.map((launch) => (
        <div className="danger-zone__row" key={launch.id}>
          <div>
            <div className="danger-zone__label">Clear go-live date</div>
            <div className="danger-zone__copy">
              {launch.name} went live {formatDay(launch.go_live_date as string)}. Clearing the date makes every
              conversion load as a pre-convert, so nothing bills, until the next non-paused publish sets a new one.
            </div>
          </div>
          <div className="danger-zone__action">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: "0.72rem" }}
              disabled={running}
              onClick={() => setConfirming({ kind: "go-live", launch })}
            >
              Clear go-live date…
            </button>
          </div>
        </div>
      ))}
        </>
      )}

      <Modal show={confirming !== null} onHide={close} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>{title()}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {confirming?.kind === "test" && (
            <p className="mb-0">
              The {testCount.toLocaleString()} test rows are deleted with their audience members and
              conversions. Nothing else changes.
            </p>
          )}

          {confirming?.kind === "go-live" && (
            <p className="mb-0">
              {confirming.launch.name} loses its go-live date of {formatDay(confirming.launch.go_live_date as string)}.
              Files load as arrival state and nothing bills until the next non-paused publish stamps a new date.
              Conversions already recorded keep their classification.
            </p>
          )}

          {confirming?.kind === "all" && (
            <>
              <p>This will remove:</p>
              <ul>
                <li>{traceCount.toLocaleString()} conversion traces</li>
                {audiences.map((a) => (
                  <li key={a.id}>
                    {a.member_count.toLocaleString()} audience members from <strong>{a.name}</strong>
                  </li>
                ))}
                <li>
                  all {populationCount.toLocaleString()} population members. The holdout is kept: everyone a
                  file brings back returns to the group they were in, and only new arrivals can join the holdout.
                  To change or re-draw it, use the control group setting.
                </li>
              </ul>
              {published && (
                <div className="alert alert-warning py-2 small">
                  This population is published. Network audiences keep everyone already uploaded: republishing
                  adds the new members but cannot remove anyone the new file drops.
                </div>
              )}
              <p>The population definition, audience rules and data files stay.</p>
              <p className="mb-1">Afterwards:</p>
              <ol>
                <li>Load a file — upload a new one, or Reprocess all files to replay the stored ones.</li>
                <li>Recalculate the audiences.</li>
                <li>Republish.</li>
              </ol>

              {requiresOverride && (
                <div className="alert alert-warning py-2 small">
                  <strong>This population has already been billed.</strong> Conversions exist in{" "}
                  {billedMonths.length === 1 ? "a closed billing month" : "closed billing months"}:{" "}
                  {billedMonths.map((m) => `${m.label} (${m.conversions.toLocaleString()} conversions)`).join(", ")}.
                  The current invoice is saved first so the numbers can be compared.
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="danger-zone-override-ack"
                      checked={overrideAck}
                      onChange={(e) => setOverrideAck(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="danger-zone-override-ack">
                      I am correcting a confirmed billing error and mean to rewrite these months.
                    </label>
                  </div>
                  <textarea
                    className="form-control form-control-sm mt-2"
                    rows={2}
                    placeholder="Why is this correction necessary? Recorded against the job and the saved invoice."
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    disabled={!overrideAck}
                  />
                </div>
              )}

              <label className="form-label mb-1" htmlFor="danger-zone-confirm-name">
                Type <strong>{populationName}</strong> to confirm
              </label>
              <input
                id="danger-zone-confirm-name"
                className="form-control form-control-sm"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                autoComplete="off"
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={close} disabled={running}>
            Cancel
          </button>
          <button type="button" className="btn btn-sm btn-danger" onClick={() => void run()} disabled={!canConfirm}>
            {confirmLabel()}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
